import React, { useEffect, useRef } from 'react';
import { startStripeCheckout } from '../utils/upgradeFlow';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MobileDesktopBanner from '../components/MobileDesktopBanner';
import { foundersFor } from '../config/foundersDiscount';
import './NaplexPrep.css';

// Counts derived from services/naplex_content.py + the in-app area map
// (map_generator._build_naplex_area_overview, 2026-06-12) — MAP-LITERAL
// semantics: lessons = topic groups (one teaching lesson each), synthesis =
// inter-group tier links, bosses = fightable boss nodes the map renders
// (one group boss per group + one tier boss per link + the area
// apothecarius), rounds = clinical rounds. Previously authored estimates.
const AREAS = [
  { name: 'Cardiovascular', color: '#ef4444', lessons: 31, synthesis: 4, bosses: 36, rounds: 6 },
  { name: 'Infectious Disease', color: '#f97316', lessons: 42, synthesis: 4, bosses: 47, rounds: 2 },
  { name: 'Endocrine', color: '#eab308', lessons: 18, synthesis: 4, bosses: 23, rounds: 3 },
  { name: 'Psychiatry', color: '#a78bfa', lessons: 15, synthesis: 4, bosses: 20, rounds: 4 },
  { name: 'Neurology', color: '#8b5cf6', lessons: 16, synthesis: 4, bosses: 21, rounds: 4 },
  { name: 'Pulmonary', color: '#0ea5e9', lessons: 22, synthesis: 4, bosses: 27, rounds: 4 },
  { name: 'Gastrointestinal', color: '#22c55e', lessons: 20, synthesis: 4, bosses: 25, rounds: 4 },
  { name: 'Pain / Inflammation', color: '#f43f5e', lessons: 16, synthesis: 4, bosses: 21, rounds: 4 },
  { name: 'Oncology', color: '#6366f1', lessons: 15, synthesis: 4, bosses: 20, rounds: 4 },
  { name: 'Renal', color: '#14b8a6', lessons: 14, synthesis: 4, bosses: 19, rounds: 4 },
  { name: 'Hematology', color: '#dc2626', lessons: 15, synthesis: 4, bosses: 20, rounds: 4 },
  { name: 'Dermatology', color: '#f59e0b', lessons: 12, synthesis: 4, bosses: 17, rounds: 4 },
  { name: 'Ophthalmology', color: '#06b6d4', lessons: 10, synthesis: 4, bosses: 15, rounds: 4 },
  { name: 'Immunology', color: '#d946ef', lessons: 17, synthesis: 4, bosses: 22, rounds: 4 },
  { name: "Women's Health", color: '#ec4899', lessons: 17, synthesis: 4, bosses: 22, rounds: 4 },
];

const CheckIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function NaplexPrep() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const observerRef = useRef(null);
  const isAliasRoute = location.pathname === '/naplex';
  const canonicalUrl = 'https://debono.ai/naplex-prep';
  const robotsValue = isAliasRoute ? 'noindex, follow' : 'index, follow';

  useEffect(() => {
    const ref = searchParams.get('ref');
    const utm_source = searchParams.get('utm_source');
    const utm_campaign = searchParams.get('utm_campaign');
    if (ref || utm_source || utm_campaign) {
      try {
        localStorage.setItem('debono_ref', JSON.stringify({
          ref, utm_source, utm_campaign, ts: Date.now()
        }));
      } catch (_) {}
    }
  }, [searchParams]);

  // Scroll-triggered animation (same pattern as Landing)
  useEffect(() => {
    const els = document.querySelectorAll('.naplex-animate');
    if (!els.length) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('naplex-visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const handleCta = () => {
    if (isAuthenticated) {
      startStripeCheckout('naplex_crunch', { fallbackNavigate: navigate });
    } else {
      navigate('/signup?naplex=1');
    }
  };

  const fNaplex = foundersFor('naplex');

  return (
    <div className="naplex-prep">
      <Helmet>
        <title>NAPLEX DLC: AI-Powered NAPLEX Prep | Debono RX</title>
        <meta name="description" content="15 therapeutic areas of NAPLEX-relevant content. Unlimited AI-generated questions, adaptive boss fights, and auto-targeted learning maps. One-time $50 purchase, permanent access." />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={robotsValue} />
        <meta property="og:title" content="NAPLEX DLC: AI-Powered NAPLEX Prep | Debono RX" />
        <meta property="og:description" content="15 therapeutic areas. Unlimited questions. Adaptive boss fights. $50 one-time, permanent access." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Debono RX" />
        <meta property="og:image" content="https://debono.ai/og/naplex.png?v=4" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="NAPLEX DLC: Debono RX pharmacy licensure prep" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://debono.ai/og/naplex.png?v=4" />
      </Helmet>

      {/* Ambient glows */}
      <div className="naplex-prep-bg">
        <div className="naplex-prep-glow naplex-prep-glow-1" />
        <div className="naplex-prep-glow naplex-prep-glow-2" />
        <div className="naplex-prep-glow naplex-prep-glow-3" />
      </div>

      {/* ===== TOP BAR (outside inner container to span full width) ===== */}
      <nav className="naplex-prep-topbar">
        <div className="naplex-prep-topbar-container">
          <Link to="/" className="naplex-prep-topbar-logo">
            <span className="naplex-prep-topbar-bono">DEBONO</span>
            <img src={`${process.env.PUBLIC_URL}/favicon-32x32.png?v=7`} alt="" className="naplex-prep-topbar-favicon" />
            <span className="naplex-prep-topbar-rx">rx</span>
          </Link>
          <div className="naplex-prep-topbar-links">
            <Link to="/pricing">Pricing</Link>
            {isAuthenticated
              ? <Link to="/dashboard" className="naplex-prep-topbar-cta">Dashboard</Link>
              : <>
                  <Link to="/login">Log In</Link>
                  <Link to="/signup?naplex=1" className="naplex-prep-topbar-cta">Sign Up</Link>
                </>
            }
          </div>
        </div>
      </nav>

      <div className="naplex-prep-inner">
        {/* ===== HERO ===== */}
        <section className="naplex-prep-hero">
          <div className="naplex-prep-badge naplex-animate">Permanent DLC · One-Time Purchase</div>
          {fNaplex && (
            <div className="naplex-prep-founders-banner naplex-animate" role="status" aria-live="polite">
              <span className="naplex-prep-founders-dot" />
              <span className="naplex-prep-founders-label">Founders Pricing</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-was">${fNaplex.orig}</span>
              <span className="naplex-prep-founders-now">${fNaplex.sale}</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-slots">first {fNaplex.slots} only</span>
            </div>
          )}
          <h1 className="naplex-animate">
            <span>NAPLEX Prep</span>, Reimagined
          </h1>
          <p className="naplex-prep-hero-sub naplex-animate">
            15 therapeutic areas of clinically accurate, NAPLEX-relevant content.
            Unlimited AI-generated questions that never repeat. Adaptive boss fights
            that target exactly what you don't know yet.
          </p>
          <div className="naplex-prep-hero-cta naplex-animate">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.naplex_dlc
                ? 'Already Unlocked'
                : `Get NAPLEX DLC · $${fNaplex ? fNaplex.sale : 50}`}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              See All Plans
            </Link>
          </div>
        </section>

        {/* ===== STAT STRIP ===== */}
        <div className="naplex-prep-stats naplex-animate">
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">15</span>
            <span className="naplex-prep-stat-label">Therapeutic Areas</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">1,419</span>
            <span className="naplex-prep-stat-label">Unique Topics</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">47</span>
            <span className="naplex-prep-stat-label">Drug Classes</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">
              {fNaplex && <span className="naplex-prep-stat-was">${fNaplex.orig}</span>}
              ${fNaplex ? fNaplex.sale : 50}
            </span>
            <span className="naplex-prep-stat-label">
              {fNaplex ? 'Founders · One-Time, Forever' : 'One-Time, Forever'}
            </span>
          </div>
        </div>

        {/* ===== HOW IT WORKS ===== */}
        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>How It Works</h2>
            <p>
              A diagnostic-first approach. Every death is a lesson. Every retry sharpens your weakest areas.
            </p>
          </div>

          <div className="naplex-prep-flow">
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--1">1</div>
              <h3>Diagnostic Gauntlet</h3>
              <p>
                Choose a therapeutic area and enter a survival-mode gauntlet.
                Face waves of pharmacotherapy questions across MCQ, select-all,
                and matching formats. You are expected to die. That's the diagnostic.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--2">2</div>
              <h3>Targeted Learning Map</h3>
              <p>
                After each gauntlet, a learning map is auto-generated from the
                specific topics you failed. Study drug classes through teaching
                sessions, cross-drug synthesis, and clinical safety nodes.
                Only what you actually need.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--3">3</div>
              <h3>Boss Mastery</h3>
              <p>
                Defeat group bosses, then tier bosses that test cross-group
                interactions (polypharmacy, shared toxicities), then the final
                Apothecarius, a boss that specifically targets your weakest
                remaining knowledge gaps.
              </p>
            </div>
          </div>
        </section>

        {/* ===== CONTENT COVERAGE — YIELD SPECTRUM ===== */}
        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>Full NAPLEX Scope. Every Yield Level.</h2>
            <p>
              Content spans from the most frequently tested high-yield topics
              down to the low-yield details that separate a passing score from
              a dominant one.
            </p>
          </div>

          <div className="naplex-prep-yield">
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">High Yield</span>
                Core Pharmacotherapy
              </h3>
              <p>The 80/20 of NAPLEX: topics tested on nearly every exam form.</p>
              <ul className="naplex-prep-yield-list">
                <li>First-line agents, dosing, and monitoring parameters</li>
                <li>Guideline-driven treatment algorithms (HTN, DM, HF, COPD)</li>
                <li>Critical drug interactions and contraindications</li>
                <li>Renal/hepatic dose adjustments</li>
                <li>Pregnancy categories and teratogenicity</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--medium">Medium Yield</span>
                Clinical Application
              </h3>
              <p>Questions that require clinical reasoning, not just recall.</p>
              <ul className="naplex-prep-yield-list">
                <li>Patient case vignettes with comorbidity management</li>
                <li>Cross-class drug interactions and polypharmacy</li>
                <li>Therapeutic drug monitoring (vancomycin, lithium, phenytoin)</li>
                <li>Adverse effect recognition and management</li>
                <li>Step-therapy and formulary considerations</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--low">Low Yield</span>
                Advanced Detail
              </h3>
              <p>The long tail: rarely tested, but enough questions exist to matter.</p>
              <ul className="naplex-prep-yield-list">
                <li>Uncommon drug classes within each therapeutic area</li>
                <li>Detailed pharmacokinetic parameters and calculations</li>
                <li>Rare but serious adverse effects (e.g., agranulocytosis risk ranking)</li>
                <li>Orphan indications and off-label uses</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">Adaptive</span>
                Weakness Targeting
              </h3>
              <p>The engine doesn't treat all content equally. It prioritizes what you don't know.</p>
              <ul className="naplex-prep-yield-list">
                <li>Boss difficulty scales with your per-topic performance data</li>
                <li>Learning maps are generated only from failed topics</li>
                <li>Apothecarius (final boss) actively targets your weakest remaining gaps</li>
                <li>Every retry generates fresh questions, so there are no answer keys to memorize</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ===== 15 THERAPEUTIC AREAS ===== */}
        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>15 Therapeutic Areas</h2>
            <p>
              Every major domain tested on the NAPLEX, each with its own drug classes,
              clinical scenarios, and boss encounters.
            </p>
          </div>

          <div className="naplex-prep-areas-grid">
            {AREAS.map((area) => (
              <div
                key={area.name}
                className="naplex-prep-area-card naplex-animate"
                style={{ '--area-color': area.color }}
              >
                <div className="naplex-prep-area-card-glow" />
                <div className="naplex-prep-area-header">
                  <div className="naplex-prep-area-dot" />
                  <div className="naplex-prep-area-name">{area.name}</div>
                </div>
                <div className="naplex-prep-area-nodes">
                  <div className="naplex-prep-area-node" role="img" aria-label={`${area.lessons} teaching lessons`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                    <span>{area.lessons}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${area.synthesis} synthesis nodes`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    <span>{area.synthesis}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${area.bosses} boss fights`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span>{area.bosses}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${area.rounds} clinical rounds`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                    <span>{area.rounds}</span>
                  </div>
                </div>
              </div>
            ))}
            {/* Totals card to fill 4x4 grid */}
            <div
              className="naplex-prep-area-card naplex-prep-area-card--totals naplex-animate"
              style={{ '--area-color': '#10b981' }}
            >
              <div className="naplex-prep-area-card-glow" />
              <div className="naplex-prep-area-header">
                <div className="naplex-prep-area-dot" />
                <div className="naplex-prep-area-name">Total Content</div>
              </div>
              <div className="naplex-prep-area-nodes">
                {/* Row 1: node type totals */}
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                  <span>{AREAS.reduce((s, a) => s + a.lessons, 0)}</span>
                  <span className="naplex-prep-area-node--label">lessons</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  <span>{AREAS.reduce((s, a) => s + a.synthesis, 0)}</span>
                  <span className="naplex-prep-area-node--label">synthesis</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  <span>{AREAS.reduce((s, a) => s + a.bosses, 0)}</span>
                  <span className="naplex-prep-area-node--label">bosses</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  <span>{AREAS.reduce((s, a) => s + a.rounds, 0)}</span>
                  <span className="naplex-prep-area-node--label">rounds</span>
                </div>
                {/* Row 2: content scope totals */}
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  <span>15</span>
                  <span className="naplex-prep-area-node--label">areas</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3"/></svg>
                  <span>1,419</span>
                  <span className="naplex-prep-area-node--label">topics</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                  <span>47</span>
                  <span className="naplex-prep-area-node--label">classes</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5"/></svg>
                  <span>{'\u221E'}</span>
                  <span className="naplex-prep-area-node--label">questions</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== NODE TYPES ===== */}
        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>What You Encounter</h2>
            <p>
              Six node types across each therapeutic area, from focused drug teaching
              to cross-class synthesis and full-area boss fights.
            </p>
          </div>

          <div className="naplex-prep-nodes">
            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(239, 68, 68, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Diagnostic Gauntlet</h3>
              <p>
                Survival-mode entry assessment. Waves of pharmacotherapy questions
                across every drug class in the area. Die to reveal gaps, then study them.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(168, 85, 247, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                </svg>
              </div>
              <h3>Drug Teaching Sessions</h3>
              <p>
                Focused lessons on individual drug classes: MOA, indications,
                side effects, monitoring, and dosing. Interactive drills woven between
                teaching blocks.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(14, 165, 233, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </div>
              <h3>Cross-Drug Synthesis</h3>
              <p>
                Comparative nodes that test reasoning across related drug classes:
                cross-reactivity, shared toxicities, spectrum coverage differences,
                and therapeutic substitution.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3>Clinical Safety Nodes</h3>
              <p>
                Focused on adverse effects, black box warnings, monitoring parameters,
                and risk management. Covers the safety-focused questions that comprise
                a significant portion of NAPLEX.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(34, 197, 94, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <h3>Clinical Rounds</h3>
              <p>
                Patient case vignettes with comorbidities, guideline-driven decision
                points, and real-world prescribing scenarios. Teaches the clinical
                reasoning NAPLEX tests beyond rote recall.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(220, 38, 38, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <h3>Boss Fights</h3>
              <p>
                HP-based combat encounters at three tiers: group bosses (single class),
                tier bosses (cross-group polypharmacy), and the Apothecarius, a final boss
                that adapts to target your weakest remaining knowledge.
              </p>
            </div>
          </div>
        </section>

        {/* ===== DIFFERENTIATORS ===== */}
        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>Not Another Question Bank</h2>
            <p>
              Traditional NAPLEX prep gives you a fixed pool of questions.
              Once you exhaust them, you are memorizing answers, not learning pharmacotherapy.
            </p>
          </div>

          <div className="naplex-prep-compare">
            <div className="naplex-prep-compare-card naplex-prep-compare-card--them naplex-animate">
              <div className="naplex-prep-compare-label">Traditional QBanks</div>
              <ul className="naplex-prep-compare-list">
                <li><XIcon /> Fixed question pool. You exhaust it, then memorize answers</li>
                <li><XIcon /> Same questions every time you retry a topic</li>
                <li><XIcon /> No adaptive targeting. You review everything equally</li>
                <li><XIcon /> Linear format: read question, pick answer, read explanation</li>
                <li><XIcon /> $300-500+/year for access</li>
              </ul>
            </div>
            <div className="naplex-prep-compare-card naplex-prep-compare-card--us naplex-animate">
              <div className="naplex-prep-compare-label">Debono NAPLEX DLC</div>
              <ul className="naplex-prep-compare-list">
                <li><CheckIcon /> AI generates unlimited unique questions from seed content</li>
                <li><CheckIcon /> Every session is fresh. Drill the same topic repeatedly, never see repeats</li>
                <li><CheckIcon /> Boss difficulty and learning maps adapt to your performance</li>
                <li><CheckIcon /> RPG combat, survival gauntlets, teaching sessions, and clinical rounds</li>
                <li><CheckIcon /> {fNaplex ? `$${fNaplex.sale} founders price (was $${fNaplex.orig})` : '$50'} once. Permanent access, no subscription</li>
              </ul>
            </div>
          </div>
        </section>

        <MobileDesktopBanner
          page="naplex-prep"
          headline="Debono's NAPLEX prep works best on desktop."
          subtext="Drop your email and we'll send you the link. Takes 2 seconds."
        />

        {/* ===== BOTTOM CTA ===== */}
        <section className="naplex-prep-bottom-cta naplex-animate">
          <h2>One price. Full NAPLEX scope. Unlimited practice.</h2>
          <p>
            Pay once, keep it forever. No subscription, no renewal, no question pool to exhaust.
          </p>
          <div className="naplex-prep-price-display">
            {fNaplex && <span className="naplex-prep-price-was">${fNaplex.orig}</span>}
            <span className="naplex-prep-price-amount">${fNaplex ? fNaplex.sale : 50}</span>
            <span className="naplex-prep-price-period">one-time</span>
          </div>
          {fNaplex && (
            <div className="naplex-prep-founders-banner naplex-prep-founders-banner--centered">
              <span className="naplex-prep-founders-dot" />
              <span className="naplex-prep-founders-label">Founders price</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-slots">first {fNaplex.slots} only</span>
            </div>
          )}
          <p className="naplex-prep-price-note">
            Includes all 15 therapeutic areas. Also included free with the annual plan ($120/yr).
          </p>
          <div className="naplex-prep-hero-cta">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.naplex_dlc ? 'Already Unlocked' : (isAuthenticated ? 'Get NAPLEX DLC' : 'Get Started')}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              Compare Plans
            </Link>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="naplex-prep-footer">
          <div className="naplex-prep-footer-links">
            <Link to="/">Home</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
